/* =========================================================
   SSSS 360° engine
   - <spin-viewer>: product page drag-to-spin, inertia, zoom, fullscreen
   - [data-spin-card]: cursor-scrubbed rotation on product cards
   - [data-spin-scroll]: scroll-scrubbed rotation (homepage hero)
   ========================================================= */
(() => {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  /* ---------- frame cache ---------- */
  const cache = new Map();
  function loadImage(src) {
    if (cache.has(src)) return cache.get(src);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(img));
      img.onerror = () => resolve(null);
      img.src = src;
    });
    cache.set(src, p);
    return p;
  }
  /* Load frames in an interleaved order (0, n/2, n/4, 3n/4 …) so a coarse
     rotation is available quickly, then fill in the gaps. */
  function interleavedOrder(n) {
    const order = [], seen = new Set();
    for (let step = Math.pow(2, Math.ceil(Math.log2(n))); step >= 1; step = step / 2) {
      for (let i = 0; i < n; i += step) { const k = Math.floor(i); if (!seen.has(k)) { seen.add(k); order.push(k); } }
    }
    return order;
  }
  function loadFrames(srcs, onEach, concurrency = 6) {
    const order = interleavedOrder(srcs.length);
    const out = new Array(srcs.length);
    let next = 0, done = 0;
    return new Promise((resolve) => {
      const pump = () => {
        if (next >= order.length) return;
        const idx = order[next++];
        loadImage(srcs[idx]).then((img) => {
          out[idx] = img; done++;
          onEach && onEach(idx, img, done / srcs.length, out);
          if (done === srcs.length) resolve(out); else pump();
        });
      };
      for (let i = 0; i < Math.min(concurrency, srcs.length); i++) pump();
    });
  }
  /* Nearest loaded frame to index i (for partially-loaded sequences). */
  function nearest(frames, i) {
    const n = frames.length;
    if (frames[i]) return frames[i];
    for (let d = 1; d < n; d++) {
      const a = frames[(i + d) % n], b = frames[(i - d + n) % n];
      if (a) return a; if (b) return b;
    }
    return null;
  }

  /* ---------- canvas helper ---------- */
  function fitCanvas(canvas) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    return { w, h, dpr };
  }
  function drawContain(ctx, img, w, h, pad = 0, zoom = 1, panX = 0, panY = 0) {
    if (!img) return;
    ctx.clearRect(0, 0, w, h);
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const s = Math.min((w * (1 - pad * 2)) / iw, (h * (1 - pad * 2)) / ih) * zoom;
    const dw = iw * s, dh = ih * s;
    ctx.drawImage(img, (w - dw) / 2 + panX, (h - dh) / 2 + panY, dw, dh);
  }

  /* =========================================================
     <spin-viewer>
     ========================================================= */
  class SpinViewer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      if (document.readyState === 'loading' || !this.querySelector('canvas')) {
        if (!this._waiting) { this._waiting = true; document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true }); }
        return;
      }
      this._init = true;
      try {
        this.frames = JSON.parse(this.dataset.frames || '[]');
        this.hires = JSON.parse(this.dataset.hires || '[]');
      } catch (e) { this.frames = []; this.hires = []; }
      if (this.frames.length < 2) return;
      this.n = this.frames.length;
      this.idx = 0; this.pos = 0; this.vel = 0;
      this.zoom = 1; this.panX = 0; this.panY = 0;
      this.loaded = new Array(this.n);
      this.hiCache = new Array(this.n);
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.poster = this.querySelector('.spin__poster');
      this.ring = this.querySelector('[data-spin-progress]');
      this.hint = this.querySelector('[data-spin-hint]');
      this.counter = this.querySelector('[data-spin-counter]');
      this.btnPlay = this.querySelector('[data-spin-play]');
      this.btnZoom = this.querySelector('[data-spin-zoom]');
      this.btnFull = this.querySelector('[data-spin-full]');
      this.scrub = this.querySelector('[data-spin-scrub]');

      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); this.start(); }
      }, { rootMargin: '200px' });
      io.observe(this);
      this.bind();
      new ResizeObserver(() => this.render()).observe(this);
    }

    start() {
      this.classList.add('is-loading');
      loadFrames(this.frames, (i, img, p, out) => {
        this.loaded = out;
        if (this.ring) this.ring.style.setProperty('--p', p.toFixed(3));
        if (i === 0) { this.render(); }
        if (p >= 0.5 && !this.classList.contains('is-ready')) this.ready();
      }).then(() => {
        this.classList.remove('is-loading');
        this.classList.add('is-loaded');
        if (!this.classList.contains('is-ready')) this.ready();
      });
    }

    ready() {
      this.classList.add('is-ready');
      this.render();
      if (this.poster) this.poster.style.opacity = '0';
      if (window.SSSS && SSSS.spinIntro && !reduceMotion) this.intro();
    }

    intro() {
      const startPos = this.pos, dist = this.n; const t0 = performance.now(), dur = 2400;
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      this.introRunning = true;
      const step = (now) => {
        if (!this.introRunning) return;
        const t = Math.min(1, (now - t0) / dur);
        this.pos = startPos + dist * ease(t);
        this.setFrame(this.pos);
        if (t < 1) requestAnimationFrame(step); else this.introRunning = false;
      };
      requestAnimationFrame(step);
    }

    setFrame(pos) {
      const i = ((Math.round(pos) % this.n) + this.n) % this.n;
      if (i === this.idx && this._drawn) return;
      this.idx = i; this._drawn = true;
      this.render();
      if (this.counter) this.counter.textContent = String(Math.round((i / this.n) * 360)).padStart(3, '0') + '°';
      if (this.scrub) this.scrub.style.setProperty('--p', (i / (this.n - 1)).toFixed(3));
    }

    render() {
      if (!this.ctx) return;
      const { w, h } = fitCanvas(this.canvas);
      let img = null;
      if (this.zoom > 1 && this.hiCache[this.idx]) img = this.hiCache[this.idx];
      img = img || nearest(this.loaded, this.idx);
      drawContain(this.ctx, img, w, h, 0.04, this.zoom, this.panX * (w / this.clientWidth || 1), this.panY * (h / this.clientHeight || 1));
    }

    play(on) {
      this.playing = on ?? !this.playing;
      this.classList.toggle('is-playing', this.playing);
      if (this.btnPlay) this.btnPlay.setAttribute('aria-pressed', String(this.playing));
      if (this.playing) {
        let last = performance.now();
        const tick = (now) => {
          if (!this.playing) return;
          const dt = now - last; last = now;
          this.pos += dt * (this.n / 6000); // one turn / 6s
          this.setFrame(this.pos);
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }

    setZoom(z, cx, cy) {
      const prev = this.zoom;
      this.zoom = Math.max(1, Math.min(3, z));
      if (this.zoom === 1) { this.panX = 0; this.panY = 0; }
      else if (cx != null) {
        const r = this.getBoundingClientRect();
        const ox = cx - r.left - r.width / 2, oy = cy - r.top - r.height / 2;
        this.panX = (this.panX - ox) * (this.zoom / prev) + ox;
        this.panY = (this.panY - oy) * (this.zoom / prev) + oy;
      }
      this.clampPan();
      this.classList.toggle('is-zoomed', this.zoom > 1);
      if (this.btnZoom) this.btnZoom.setAttribute('aria-pressed', String(this.zoom > 1));
      if (this.zoom > 1) this.loadHi(this.idx);
      this.render();
    }
    clampPan() {
      const mx = (this.clientWidth * (this.zoom - 1)) / 2, my = (this.clientHeight * (this.zoom - 1)) / 2;
      this.panX = Math.max(-mx, Math.min(mx, this.panX));
      this.panY = Math.max(-my, Math.min(my, this.panY));
    }
    loadHi(i) {
      if (!this.hires[i] || this.hiCache[i]) return;
      loadImage(this.hires[i]).then((img) => { this.hiCache[i] = img; if (this.idx === i) this.render(); });
    }

    bind() {
      let dragging = false, lastX = 0, lastY = 0, lastT = 0, moved = 0;
      const pointers = new Map(); let pinch0 = 0, zoom0 = 1;
      const pxPerFrame = () => Math.max(4, this.clientWidth / (this.n * 1.15));

      this.canvas.addEventListener('pointerdown', (e) => {
        pointers.set(e.pointerId, e);
        this.canvas.setPointerCapture(e.pointerId);
        this.introRunning = false; this.play(false);
        if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          pinch0 = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY); zoom0 = this.zoom;
          return;
        }
        dragging = true; moved = 0; this.vel = 0;
        lastX = e.clientX; lastY = e.clientY; lastT = performance.now();
        this.classList.add('is-dragging');
        if (this.hint) this.hint.classList.add('is-gone');
      });
      this.canvas.addEventListener('pointermove', (e) => {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, e);
        if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
          this.setZoom(zoom0 * (d / pinch0), (a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
          return;
        }
        if (!dragging) return;
        const dx = e.clientX - lastX, dy = e.clientY - lastY, now = performance.now();
        moved += Math.abs(dx) + Math.abs(dy);
        if (this.zoom > 1) {
          this.panX += dx; this.panY += dy; this.clampPan(); this.render();
        } else {
          const df = -dx / pxPerFrame();
          this.pos += df;
          this.vel = df / Math.max(1, now - lastT);
          this.setFrame(this.pos);
        }
        lastX = e.clientX; lastY = e.clientY; lastT = now;
      });
      const end = (e) => {
        pointers.delete(e.pointerId);
        if (!dragging) return;
        dragging = false;
        this.classList.remove('is-dragging');
        if (this.zoom === 1 && Math.abs(this.vel) > 0.002 && !reduceMotion) {
          let v = this.vel * 16; // frames per frame-tick
          const glide = () => {
            if (dragging || Math.abs(v) < 0.02) return;
            this.pos += v; v *= 0.94; this.setFrame(this.pos);
            requestAnimationFrame(glide);
          };
          requestAnimationFrame(glide);
        }
        if (this.zoom > 1) this.loadHi(this.idx);
      };
      this.canvas.addEventListener('pointerup', end);
      this.canvas.addEventListener('pointercancel', end);
      this.canvas.addEventListener('dblclick', (e) => this.setZoom(this.zoom > 1 ? 1 : 2.2, e.clientX, e.clientY));
      this.canvas.addEventListener('wheel', (e) => {
        if (!(e.ctrlKey || this.zoom > 1 || document.fullscreenElement === this)) return;
        e.preventDefault();
        this.setZoom(this.zoom * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX, e.clientY);
      }, { passive: false });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { this.pos += 1; this.setFrame(this.pos); e.preventDefault(); }
        if (e.key === 'ArrowLeft') { this.pos -= 1; this.setFrame(this.pos); e.preventDefault(); }
        if (e.key === ' ') { this.play(); e.preventDefault(); }
      });
      this.btnPlay && this.btnPlay.addEventListener('click', () => this.play());
      this.btnZoom && this.btnZoom.addEventListener('click', () => this.setZoom(this.zoom > 1 ? 1 : 2));
      this.btnFull && this.btnFull.addEventListener('click', () => {
        const el = this.closest('[data-spin-stage]') || this;
        if (document.fullscreenElement) document.exitFullscreen();
        else if (el.requestFullscreen) el.requestFullscreen().catch(() => el.classList.toggle('is-fullscreen'));
        else el.classList.toggle('is-fullscreen');
      });
      document.addEventListener('fullscreenchange', () => requestAnimationFrame(() => this.render()));
    }
  }
  if (!customElements.get('spin-viewer')) customElements.define('spin-viewer', SpinViewer);

  /* =========================================================
     Card spin — cursor position scrubs the rotation
     ========================================================= */
  function initCard(card) {
    if (card._spin) return; card._spin = true;
    let frames; try { frames = JSON.parse(card.dataset.spinCard); } catch (e) { return; }
    if (!frames || frames.length < 4) return;
    const holder = card.querySelector('.card__spin');
    const bar = card.querySelector('.card__spinbar');
    const media = card.querySelector('.card__media');
    if (!holder || !media) return;
    const img = document.createElement('img');
    img.alt = ''; img.decoding = 'async';
    holder.appendChild(img);
    let loaded = new Array(frames.length), started = false, cur = -1, auto = null;

    const show = (i) => {
      i = ((i % frames.length) + frames.length) % frames.length;
      if (i === cur) return;
      const f = nearest(loaded, i);
      if (!f) return;
      cur = i; img.src = f.src;
      if (bar) bar.style.setProperty('--p', (i / (frames.length - 1)).toFixed(3));
    };
    const ensure = () => {
      if (started) return; started = true;
      loadFrames(frames, (i, f, p, out) => { loaded = out; }, 4);
    };

    if (canHover) {
      // Listen on the card: the title link's full-card overlay sits above the media.
      card.addEventListener('pointerenter', () => ensure());
      card.addEventListener('pointermove', (e) => {
        const r = media.getBoundingClientRect();
        if (e.clientY > r.bottom || e.clientY < r.top) { card.classList.remove('is-spinning'); cur = -1; return; }
        card.classList.add('is-spinning');
        const x = Math.max(0, Math.min(0.999, (e.clientX - r.left) / r.width));
        show(Math.floor(x * frames.length));
      });
      card.addEventListener('pointerleave', () => { card.classList.remove('is-spinning'); cur = -1; });
    } else if (!saveData && !reduceMotion) {
      // Touch: slow auto-rotation while the card sits in the middle of the screen
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            ensure();
            card.classList.add('is-spinning');
            let i = 0;
            clearInterval(auto);
            auto = setInterval(() => show(i++), 110);
          } else {
            clearInterval(auto); card.classList.remove('is-spinning'); cur = -1;
          }
        });
      }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
      io.observe(card);
    }
  }
  function scanCards(root = document) {
    root.querySelectorAll('[data-spin-card]').forEach(initCard);
  }

  /* =========================================================
     Scroll-scrubbed hero spin
     ========================================================= */
  function initScroll(section) {
    if (section._spin) return; section._spin = true;
    let frames; try { frames = JSON.parse(section.dataset.spinScroll); } catch (e) { return; }
    const canvas = section.querySelector('canvas');
    if (!canvas || !frames || !frames.length) return;
    const ctx = canvas.getContext('2d');
    const turns = parseFloat(section.dataset.turns || '1');
    let loaded = new Array(frames.length), idx = 0, ticking = false, visible = true;

    const draw = () => {
      const { w, h } = fitCanvas(canvas);
      drawContain(ctx, nearest(loaded, idx), w, h, 0.02);
    };
    const update = () => {
      ticking = false;
      const r = section.getBoundingClientRect();
      const total = r.height - innerHeight;
      const p = Math.max(0, Math.min(1, total > 0 ? -r.top / total : 0));
      section.style.setProperty('--progress', p.toFixed(4));
      const n = frames.length;
      const next = Math.floor(p * n * turns) % n;
      if (next !== idx) { idx = next; draw(); }
    };
    loadFrames(frames, (i, f, p, out) => {
      loaded = out;
      if (i === 0) { section.classList.add('is-ready'); draw(); }
      section.style.setProperty('--load', p.toFixed(3));
    }, 6).then(() => section.classList.add('is-loaded'));

    new IntersectionObserver((e) => { visible = e[0].isIntersecting; }).observe(section);
    addEventListener('scroll', () => {
      if (!visible || ticking) return;
      ticking = true; requestAnimationFrame(update);
    }, { passive: true });
    new ResizeObserver(() => { draw(); update(); }).observe(canvas);
    update();

    // Idle drift when the hero is visible but the user isn't scrolling (reduced motion respected)
    if (!reduceMotion) {
      let lastScroll = performance.now();
      addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
      const drift = () => {
        if (visible && performance.now() - lastScroll > 1600 && section.classList.contains('is-loaded') && window.scrollY < 40) {
          idx = (idx + 1) % frames.length; draw();
          setTimeout(() => requestAnimationFrame(drift), 90);
        } else setTimeout(() => requestAnimationFrame(drift), 400);
      };
      drift();
    }
  }

  function scanAll(root = document) {
    scanCards(root);
    root.querySelectorAll('[data-spin-scroll]').forEach(initScroll);
  }
  window.SSSSSpin = { scan: scanAll, loadImage };
  if (document.readyState !== 'loading') scanAll(); else document.addEventListener('DOMContentLoaded', () => scanAll());
  document.addEventListener('ssss:content', (e) => scanAll(e.target || document));
  document.addEventListener('shopify:section:load', (e) => scanAll(e.target));
})();
